import type { BootOptions, IdentifyOptions, AttendEvent, Message, SessionResponse } from './types';
import { WidgetStore } from './state/store';
import { WidgetStorage } from './state/storage';
import { ApiClient, ApiError } from './api/client';
import { SSEManager } from './api/sse';
import { createShadowRoot } from './ui/shadow-root';
import { Trigger } from './ui/trigger';
import { Panel } from './ui/panel';
import { getStyles } from './styles.css';
import { generateVisitorId, normalizeVisitorId } from './utils/ids';
import { isDomainAllowed } from './utils/domain';

export class AttendeApp {
  private tenant: string;
  private store: WidgetStore;
  private storage: WidgetStorage;
  private api: ApiClient;
  private sse: SSEManager | null = null;
  private root: ShadowRoot | null = null;
  private host: HTMLDivElement | null = null;
  private trigger: Trigger | null = null;
  private panel: Panel | null = null;
  private autoOpenTimer: number | null = null;
  private proactiveTimer: number | null = null;
  private typingSafetyTimer: number | null = null;
  private streamRecoveryTimer: number | null = null;
  private visitorId = '';
  /** ids de mensagens já exibidas (histórico + enviadas + recebidas) — evita duplicar ao ressincronizar. */
  private knownMessageIds = new Set<number>();
  /** ids locais (negativos) das mensagens otimistas, até o servidor confirmar. */
  private tempSeq = 0;
  private sessionPromise: Promise<void> | null = null;
  /** Envios em série: a ordem de saída é a ordem em que o visitante digitou. */
  private sendChain: Promise<void> = Promise.resolve();
  private destroyed = false;
  /** `open`/`hide` chamados antes de o painel existir (comandos da fila pré-carga). */
  private pendingOpen = false;
  private pendingHidden = false;

  // [P33] fallback: se o backend nunca emitir 'typing_stop', o indicador de "digitando" ficaria
  // travado para sempre. Zera após esse prazo.
  private static readonly TYPING_SAFETY_TIMEOUT_MS = 12_000;
  private static readonly STREAM_RECOVERY_DELAY_MS = 30_000;

  constructor(options: BootOptions) {
    this.tenant = options.tenant;
    this.store = new WidgetStore();
    this.storage = new WidgetStorage(options.tenant);
    this.api = new ApiClient(options.apiBase);
  }

  // [P6F-M04] TTL de 90 dias para visitorId — evita rastreamento cross-session indefinido (LGPD/GDPR)
  private static readonly VISITOR_ID_TTL_MS = 90 * 24 * 60 * 60 * 1000;

  private loadVisitorId(): string {
    const raw = this.storage.get('visitorId');
    if (raw) {
      try {
        const parsed = JSON.parse(raw) as { v: unknown; exp: number };
        // Aceita UUID v4 (formato exigido pela API) e migra o legado `vis_<uuid>` sem trocar a identidade.
        const id = normalizeVisitorId(parsed.v);
        if (id && Date.now() < parsed.exp) {
          if (id !== parsed.v) {
            this.storage.set('visitorId', JSON.stringify({ v: id, exp: parsed.exp }));
          }
          return id;
        }
      } catch {
        // formato legado (string pura) ou corrompido — regenera
      }
      this.storage.remove('visitorId');
    }
    const newId = generateVisitorId();
    this.storage.set('visitorId', JSON.stringify({ v: newId, exp: Date.now() + AttendeApp.VISITOR_ID_TTL_MS }));
    return newId;
  }

  async boot(): Promise<void> {
    // 1. visitorId (UUID v4, com TTL de 90 dias)
    this.visitorId = this.loadVisitorId();

    // 2. Config
    let config;
    try {
      config = await this.api.fetchConfig(this.tenant);
    } catch (err) {
      console.warn('[attende] Failed to fetch widget config:', err);
      return;
    }

    // 3. Domínio (checagem de cortesia no cliente; quem garante é o CORS/allowedDomains da API)
    const hostname = window.location.hostname;
    if (!isDomainAllowed(hostname, config.config.allowedDomains)) {
      console.warn(`[attende] Domain not allowed: ${hostname}`);
      return;
    }

    this.store.setState({ config, online: config.status.online });

    // 4. Shadow root
    const { host, root } = createShadowRoot();
    this.host = host;
    this.root = root;

    // 5. CSS
    const style = document.createElement('style');
    style.textContent = getStyles(
      config.config.brandAccent,
      config.config.brandAccentSecondary,
      config.config.behaviorPosition,
    );
    root.appendChild(style);

    // 6. Trigger
    this.trigger = new Trigger(root, config, () => this.togglePanel());

    // 7. Painel (oculto)
    this.panel = new Panel(config, {
      onMinimize: () => this.closePanel(),
      onClose: () => this.closePanel(),
      onSend: (text) => this.sendMessage(text),
      onQuickReplyMessage: (content) => {
        void this.sendMessage(content);
      },
      onWhatsApp: (value, _label) => {
        const waUrl = value.startsWith('https://wa.me') ? value : `https://wa.me/${value.replace(/[^\d]/g, '')}`;
        this.panel?.appendHandoffCard(waUrl);
      },
    });
    root.appendChild(this.panel.el);

    // 8. Restaura estado aberto (ou atende um `open` pedido antes do carregamento)
    const wasOpen = this.storage.get('isOpen') === '1' || this.pendingOpen;
    this.pendingOpen = false;
    if (wasOpen) {
      void this.openPanel();
    }
    if (this.pendingHidden) {
      this.hide();
    }

    // 9. Auto-open
    const autoOpen = config.config.behaviorAutoOpenAfter;
    if (autoOpen !== null && autoOpen > 0 && !wasOpen) {
      this.autoOpenTimer = window.setTimeout(() => {
        void this.openPanel();
      }, autoOpen * 1000);
    }

    // 10. Mensagem proativa
    const proactiveMsg = config.config.behaviorProactiveMessage;
    if (proactiveMsg && !wasOpen) {
      this.proactiveTimer = window.setTimeout(() => {
        this.trigger?.showProactive(proactiveMsg);
      }, 5000);
    }

    this.store.on('unreadCount', (count) => {
      this.trigger?.setUnread(count);
    });

    // 11. 'ready'
    this.store.emit('ready');
  }

  async openPanel(): Promise<void> {
    if (!this.panel || !this.root) {
      this.pendingOpen = true; // ainda carregando: abre assim que o painel existir
      return;
    }

    // Valida/cria sessão no backend na primeira abertura da página
    if (!this.store.getState().sessionStarted) {
      try {
        await this.ensureSession();
      } catch (err) {
        console.warn('[attende] Session creation failed:', err);
        this.panel.showError(() => void this.openPanel());
        this.panel.open();
        this.store.setState({ isOpen: true });
        this.trigger?.setOpen(true);
        return;
      }
    }

    this.panel.open();
    this.store.setState({ isOpen: true });
    this.storage.set('isOpen', '1');
    this.store.resetUnread();
    this.trigger?.setOpen(true);
    this.panel.focusComposer();
    this.store.emit('open');
  }

  closePanel(): void {
    this.pendingOpen = false;
    if (!this.panel) return;
    this.panel.close();
    this.store.setState({ isOpen: false });
    this.storage.set('isOpen', '0');
    this.trigger?.setOpen(false);
    this.store.emit('close');
  }

  togglePanel(): void {
    if (this.store.getState().isOpen) {
      this.closePanel();
    } else {
      void this.openPanel();
    }
  }

  show(): void {
    this.pendingHidden = false;
    if (this.host) {
      this.host.style.display = '';
    }
    this.store.setState({ isVisible: true });
  }

  hide(): void {
    this.pendingHidden = true;
    if (this.host) {
      this.host.style.display = 'none';
    }
    this.store.setState({ isVisible: false });
  }

  // ── Sessão ────────────────────────────────────────────────────────────────────────────────

  /** Cria a sessão (ou renova o token). Chamadas simultâneas compartilham a mesma requisição. */
  private ensureSession(force = false): Promise<void> {
    if (!force && this.store.getState().sessionStarted) return Promise.resolve();
    if (!this.sessionPromise) {
      this.sessionPromise = this.api
        .createSession({
          tenant: this.tenant,
          visitorId: this.visitorId,
          url: window.location.href,
          userAgent: navigator.userAgent,
          language: navigator.language,
          referrer: document.referrer,
        })
        .then((session) => this.applySession(session))
        .finally(() => {
          this.sessionPromise = null;
        });
    }
    return this.sessionPromise;
  }

  private applySession(session: SessionResponse): void {
    const firstSession = !this.store.getState().sessionStarted;
    const hadConversation = Boolean(this.store.getState().conversationId ?? this.storage.get('conversationId'));

    this.store.setState({
      sessionToken: session.sessionToken,
      conversationId: session.conversationId,
      sessionStarted: true,
    });
    this.storage.set('sessionToken', session.sessionToken);
    if (session.conversationId) {
      this.storage.set('conversationId', session.conversationId);
    } else {
      this.storage.remove('conversationId');
    }
    this.sse?.setUrl(this.api.getStreamUrl(session.sessionToken));

    if (firstSession) {
      // Histórico da conversa em andamento (vazio se a anterior foi encerrada)
      session.messages.forEach((m) => this.knownMessageIds.add(m.id));
      if (session.messages.length > 0) {
        this.panel?.renderMessages(session.messages);
        this.store.setState({ messages: session.messages });
      }
      this.connectSSE(session.sessionToken);
      void this.pushIdentity();
      return;
    }

    // Renovação/ressincronização: só entram as mensagens que ainda não conhecemos
    this.mergeMessages(session.messages);
    if (hadConversation && !session.conversationId) {
      this.panel?.appendNotice('Atendimento encerrado. Envie uma mensagem para iniciar uma nova conversa.');
    }
  }

  /** Acrescenta mensagens do servidor que o painel ainda não mostra (perdidas offline / outra aba). */
  private mergeMessages(messages: Message[]): void {
    for (const msg of messages) {
      if (this.knownMessageIds.has(msg.id)) continue;
      this.knownMessageIds.add(msg.id);
      this.store.addMessage(msg);
      this.panel?.appendMessage(msg);
      if (msg.from === 'agent') {
        if (!this.store.getState().isOpen) this.store.incrementUnread();
        this.store.emit('message', { from: msg.from, content: msg.content });
      }
    }
  }

  // ── Envio ─────────────────────────────────────────────────────────────────────────────────

  async sendMessage(content: string): Promise<void> {
    if (!this.panel) return;
    const text = content.trim();
    if (!text) return;

    // Mensagem otimista com id local negativo; o servidor devolve o id definitivo.
    const tempId = -(++this.tempSeq);
    const clientMessageId = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
    const tempMsg: Message = {
      id: tempId,
      from: 'visitor',
      name: null,
      content: text,
      createdAt: new Date().toISOString(),
      status: 'sending',
    };
    this.store.addMessage(tempMsg);
    this.panel.appendMessage(tempMsg);

    const run = this.sendChain.then(() => this.deliver(tempId, text, clientMessageId));
    this.sendChain = run.catch(() => undefined);
    await run;
  }

  /** Entrega uma mensagem: renova o token se expirou (401) e oferece "tentar de novo" se falhar. */
  private async deliver(tempId: number, content: string, clientMessageId: string): Promise<void> {
    if (!this.panel || this.destroyed) return;
    this.panel.setMessageStatus(tempId, 'sending');

    try {
      await this.ensureSession();
      let token = this.store.getState().sessionToken;
      if (!token) throw new ApiError(0, 'no session token');

      let result;
      try {
        result = await this.api.sendMessage(token, content, clientMessageId);
      } catch (err) {
        if (!(err instanceof ApiError) || err.status !== 401) throw err;
        await this.ensureSession(true); // token expirado: renova e tenta uma vez
        token = this.store.getState().sessionToken;
        if (!token) throw err;
        result = await this.api.sendMessage(token, content, clientMessageId);
      }

      this.knownMessageIds.add(result.messageId);
      this.panel.replaceMessageId(tempId, result.messageId);
      this.panel.setMessageStatus(result.messageId, 'sent');
      this.updateLocalMessage(tempId, { id: result.messageId, status: 'sent' });

      this.store.setState({ conversationId: result.conversationId });
      this.storage.set('conversationId', result.conversationId);
      if (result.sessionToken) {
        this.store.setState({ sessionToken: result.sessionToken });
        this.storage.set('sessionToken', result.sessionToken);
        this.sse?.setUrl(this.api.getStreamUrl(result.sessionToken));
      }
      this.store.emit('message', { from: 'visitor', content });
    } catch (err) {
      console.warn('[attende] Failed to send message:', err);
      this.updateLocalMessage(tempId, { status: 'failed' });
      const reason =
        err instanceof ApiError && err.status === 429 ? 'Muitas mensagens — aguarde e tente de novo' : 'Não enviada';
      this.panel.setMessageStatus(
        tempId,
        'failed',
        () => {
          this.sendChain = this.sendChain.then(() => this.deliver(tempId, content, clientMessageId)).catch(() => undefined);
        },
        reason,
      );
    } finally {
      this.panel?.focusComposer();
    }
  }

  private updateLocalMessage(id: number, patch: Partial<Message>): void {
    const messages = this.store.getState().messages.map((m) => (m.id === id ? { ...m, ...patch } : m));
    this.store.setState({ messages });
  }

  // ── Identificação ─────────────────────────────────────────────────────────────────────────

  async identify(opts: IdentifyOptions): Promise<void> {
    this.storage.set('userId', opts.userId);
    if (opts.email) this.storage.set('userEmail', opts.email);
    if (opts.name) this.storage.set('userName', opts.name);
    if (opts.hmac) this.storage.set('userHmac', opts.hmac);

    // Sessão ainda não iniciada: os dados ficam guardados e seguem junto com a abertura do painel
    if (this.store.getState().sessionStarted) {
      await this.pushIdentity();
    }
  }

  private async pushIdentity(): Promise<void> {
    const userId = this.storage.get('userId');
    if (!userId) return;
    const params = {
      userId,
      ...(this.storage.get('userEmail') ? { email: this.storage.get('userEmail') as string } : {}),
      ...(this.storage.get('userName') ? { name: this.storage.get('userName') as string } : {}),
      ...(this.storage.get('userHmac') ? { hmac: this.storage.get('userHmac') as string } : {}),
    };
    try {
      const token = this.store.getState().sessionToken;
      if (!token) return;
      try {
        await this.api.identify(token, params);
      } catch (err) {
        if (!(err instanceof ApiError) || err.status !== 401) throw err;
        await this.ensureSession(true);
        const fresh = this.store.getState().sessionToken;
        if (fresh) await this.api.identify(fresh, params);
      }
    } catch (err) {
      console.warn('[attende] Identify failed:', err);
    }
  }

  shutdown(): void {
    this.destroyed = true;
    if (this.autoOpenTimer !== null) clearTimeout(this.autoOpenTimer);
    if (this.proactiveTimer !== null) clearTimeout(this.proactiveTimer);
    if (this.typingSafetyTimer !== null) clearTimeout(this.typingSafetyTimer);
    if (this.streamRecoveryTimer !== null) clearTimeout(this.streamRecoveryTimer);
    this.sse?.destroy();
    this.storage.clear();
    if (this.host) {
      this.host.remove();
      this.host = null;
      this.root = null;
    }
    this.trigger = null;
    this.panel = null;
  }

  onEvent(event: AttendEvent, cb: (...args: unknown[]) => void): void {
    this.store.onEvent(event, cb);
  }

  offEvent(event: AttendEvent, cb: (...args: unknown[]) => void): void {
    this.store.offEvent(event, cb);
  }

  // ── Tempo real (SSE) ──────────────────────────────────────────────────────────────────────

  private connectSSE(sessionToken: string): void {
    this.sse?.destroy();
    const url = this.api.getStreamUrl(sessionToken);
    this.sse = new SSEManager(url, {
      onMessage: (data) => this.handleSSEMessage(data),
      onReconnect: (_attempt) => {
        // reconexão silenciosa
      },
      // Voltou depois de uma queda/aba oculta: busca o que o servidor entregou enquanto estávamos fora.
      onReconnected: () => {
        void this.resync();
      },
      // Esgotou as tentativas (token expirado, API fora): renova a sessão e tenta de novo mais tarde.
      onMaxRetriesReached: () => {
        this.scheduleStreamRecovery();
      },
    });
    this.sse.connect();
  }

  private async resync(): Promise<void> {
    if (this.destroyed) return;
    try {
      await this.ensureSession(true); // applySession mescla só o que faltava
    } catch (err) {
      console.warn('[attende] Resync failed:', err);
    }
  }

  private scheduleStreamRecovery(): void {
    if (this.destroyed || this.streamRecoveryTimer !== null) return;
    this.streamRecoveryTimer = window.setTimeout(() => {
      this.streamRecoveryTimer = null;
      if (this.destroyed) return;
      this.ensureSession(true)
        .then(() => {
          const token = this.store.getState().sessionToken;
          if (token) this.connectSSE(token);
        })
        .catch(() => this.scheduleStreamRecovery());
    }, AttendeApp.STREAM_RECOVERY_DELAY_MS);
  }

  private handleSSEMessage(data: Record<string, unknown>): void {
    const event = data['event'] as string | undefined;

    switch (event) {
      case 'message': {
        const msg = data['message'] as Message | undefined;
        if (!msg || typeof msg.content !== 'string') break;
        if (typeof msg.id === 'number') {
          if (this.knownMessageIds.has(msg.id)) break; // já exibida (histórico/ressincronização)
          this.knownMessageIds.add(msg.id);
        }
        // [P33] uma mensagem chegando implica que o agente parou de digitar
        this.clearTypingIndicator();
        this.store.addMessage(msg);
        this.panel?.appendMessage(msg);
        if (!this.store.getState().isOpen) {
          this.store.incrementUnread();
        }
        this.store.emit('message', { from: msg.from, content: msg.content });
        break;
      }
      case 'conversation_closed':
        // O atendente encerrou: a próxima mensagem do visitante abre uma conversa nova
        this.clearTypingIndicator();
        this.store.setState({ conversationId: null });
        this.storage.remove('conversationId');
        this.panel?.appendNotice('Atendimento encerrado. Envie uma mensagem para iniciar uma nova conversa.');
        break;
      case 'typing_start':
        this.store.setState({ agentTyping: true });
        this.panel?.showTyping();
        // [P33] failsafe: se o 'typing_stop' nunca chegar, o indicador não pode ficar travado
        if (this.typingSafetyTimer !== null) clearTimeout(this.typingSafetyTimer);
        this.typingSafetyTimer = window.setTimeout(() => {
          this.clearTypingIndicator();
        }, AttendeApp.TYPING_SAFETY_TIMEOUT_MS);
        break;
      case 'typing_stop':
        this.clearTypingIndicator();
        break;
      case 'status': {
        const online = data['online'] as boolean | undefined;
        if (typeof online === 'boolean') {
          this.store.setState({ online });
        }
        break;
      }
      default:
        // 'ready' e 'ping' (keep-alive) não exigem ação
        break;
    }
  }

  private clearTypingIndicator(): void {
    if (this.typingSafetyTimer !== null) {
      clearTimeout(this.typingSafetyTimer);
      this.typingSafetyTimer = null;
    }
    if (this.store.getState().agentTyping) {
      this.store.setState({ agentTyping: false });
    }
    this.panel?.hideTyping();
  }
}
